import { useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query'
import type { TenantRoleDto, TenantRoleListDto } from '@lib/dto/tenant-role.dto'
import type { TenantRoleInput } from '@lib/input/tenant-role.input'
import { tenantRoleService } from '@/services/TenantRoleService'
import { tenantRoleKeys } from './queryKeys'
import { refreshPeople } from './useUsers'

/** The Roles page: the company's roles and how many people hold each. */
export function useTenantRoles(): UseQueryResult<TenantRoleListDto> {
  return useQuery({ queryKey: tenantRoleKeys.list(), queryFn: () => tenantRoleService.list() })
}

export function useTenantRole(id: string, enabled: boolean): UseQueryResult<TenantRoleDto> {
  return useQuery({ queryKey: tenantRoleKeys.detail(id), queryFn: () => tenantRoleService.get(id), enabled: enabled && id !== '' })
}

/**
 * Any change to a role changes the list it sits in, so each one refreshes it,
 * and the people who hold it: their role reads differently, and a role made
 * Company-wide takes their venues away.
 */
function useRoleChange<V>(run: (variables: V) => Promise<TenantRoleDto>): UseMutationResult<TenantRoleDto, Error, V> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: run,
    onSuccess: (role) => {
      queryClient.setQueryData(tenantRoleKeys.detail(role.id), role)
      void queryClient.invalidateQueries({ queryKey: tenantRoleKeys.list() })
      refreshPeople(queryClient)
    },
  })
}

export function useCreateTenantRole(): UseMutationResult<TenantRoleDto, Error, TenantRoleInput> {
  return useRoleChange((input: TenantRoleInput) => tenantRoleService.create(input))
}

export function useUpdateTenantRole(id: string): UseMutationResult<TenantRoleDto, Error, TenantRoleInput> {
  return useRoleChange((input: TenantRoleInput) => tenantRoleService.update(id, input))
}

export function useResetTenantRole(): UseMutationResult<TenantRoleDto, Error, string> {
  return useRoleChange((id: string) => tenantRoleService.reset(id))
}

export function useDeleteTenantRole(): UseMutationResult<void, Error, string> {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => tenantRoleService.remove(id),
    onSuccess: (_done, id) => {
      queryClient.removeQueries({ queryKey: tenantRoleKeys.detail(id) })
      void queryClient.invalidateQueries({ queryKey: tenantRoleKeys.list() })
      refreshPeople(queryClient)
    },
  })
}
